import React, { useEffect } from "react";
import { fetchAuthSession } from "@aws-amplify/auth";
import { Amplify } from "aws-amplify";

import { Authenticator, useTheme, ThemeProvider } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";

import App from "./App";
import { LoginWrapper, LoginSignInWrapper } from "./UIKit/styles";

import {
  AWS_COGNITO_USER_POOL_CLIENT_ID,
  AWS_COGNITO_USER_POOL_ID,
} from "./constants";

import { Header, Footer } from "./Components";

import { InfoBox } from "./UIKit/components";

import {
  Heading2,
  Heading5,
  PageWrapper,
  PageSection,
  ParagraphText,
  PrivacyLink,
} from "./UIKit/styles";

Amplify.configure({
  Auth: {
    Cognito: {
      // NOTE: These will need to be set...
      userPoolClientId: AWS_COGNITO_USER_POOL_CLIENT_ID,
      userPoolId: AWS_COGNITO_USER_POOL_ID,
      signUpVerificationMethod: "code",
      loginWith: {
        email: true,
      },
    },
  },
});

const Login = () => {
  const [viewPrivacy, setViewPrivacy] = React.useState(false);
  const [showDisclaimer, setShowDisclaimer] = React.useState(true);

  const getUserBearerToken = async () => {
    const session = await fetchAuthSession();

    return session.tokens.idToken.toString();
  };

  const getInitialState = () => {
    if (window.location.href.toLowerCase().includes("/signup")) return "signUp";

    return "login";
  };

  return (
    <LoginWrapper>
      <Header />
      <LoginSignInWrapper data-testid="login-sign-in-wrapper">
        {showDisclaimer && (
          <PageWrapper type="disclaimer">
            <Heading2>
            Welcome,
            </Heading2>
            <ParagraphText>
              This tool has been developed to support you to measure and manage your carbon footprint i.e. greenhouse gas emissions. You will be able to see what your emissions are for each category (e.g. fuel, fertiliser) and what your hot spots are, and also how you compare to an industry average. You will also be able to change your inputs on a “What-if” page to see what impact this has on your overall emissions. This is the first version of the tool and we welcome any feedback which will help us to make it a better experience for you. Please use the feedback link at the bottom of any of the pages in the tool.
              </ParagraphText>
            <ParagraphText>
            To get started, create a new account using the 'Create account' tab. Or alternatively sign in using your email and password. If you've forgotten your password you can retrieve it using the 'Forgot your password?' link below.
            </ParagraphText>
          </PageWrapper>
        )}
        <ThemeProvider>
          <Authenticator
            initialState={getInitialState()}
            signUpAttributes={["email", "given_name", "family_name"]}
          >
            {({ signOut, user }) => (
              <App
                signOut={signOut}
                user={user}
                getUserBearerToken={getUserBearerToken}
                setShowDisclaimer={setShowDisclaimer}
              ></App>
            )}
          </Authenticator>
        </ThemeProvider>
        {showDisclaimer && (
          <PageWrapper type="disclaimer">
            <Heading2>Disclaimer</Heading2>
            <PageSection type="disclaimer">              
              <ParagraphText>
              By using this tool you consent to Zespri collecting, using and storing your personal information in accordance with the Privacy Notice.
              </ParagraphText>
              <PrivacyLink
                onClick={() => {
                  setViewPrivacy(!viewPrivacy);
                }}
              >
                View Privacy Notice
              </PrivacyLink>
            </PageSection>
            {viewPrivacy && (
              <PageSection type="disclaimer">
                <InfoBox>
                  <Heading5 data-testid="privacy-notice-heading">
                    Privacy Notice
                  </Heading5>
                  <ParagraphText>Zespri Group Limited (“Zespri”) would like to collect your orchard information for the purposes of more accurately calculating and reporting industry emissions. Your individual data will be kept confidential. We will hold your personal information while you actively use this tool and following this for as long as necessary for the purposes it was collected.</ParagraphText>
                  <ParagraphText>
                    We use a trusted third-party provider, GPSit, to collect and
                    store your information securely. Your personal information
                    will be managed in accordance with{" "}
                    <a
                      href="https://gpsit.co.nz/privacy_policy"
                      target="_blank"
                    >
                      GPSit's Privacy Policy
                    </a>
                    .
                  </ParagraphText>
                  <ParagraphText>
                    You have rights over the information Zespri holds about you.
                    You may withdraw your consent for us to use your personal
                    information at any time. You are entitled to ask for a copy
                    of the personal information we hold about you, and to ask us
                    to correct it if it's wrong. For information about your
                    privacy rights, please see our{" "}
                    <a
                      href="https://www.zespri.com/en-NZ/privacypolicy"
                      target="_blank"
                    >
                      Privacy Statement
                    </a>
                    . To make a complaint or to exercise your privacy rights,
                    contact our Global Privacy Team at{" "}
                    <a href="mailto:privacy@zespri.com">privacy@zespri.com</a>.
                    You are also entitled to complain to the relevant data
                    protection authority.
                  </ParagraphText>
                </InfoBox>
              </PageSection>
            )}
          </PageWrapper>
        )}
      </LoginSignInWrapper>
      {showDisclaimer && (<Footer />)}
    </LoginWrapper>
  );
};

export default Login;
